/**
 * Escalation causes and pricing (RB §7.2, §7.1.1, §7.3; §7.7).
 *
 * Diff `ESCALATION_CAUSES` against RB §7.2 row by row.
 *
 * The pricing model, in one place:
 *
 *  1. A phase **banks** an entry when it causes one, because only that phase knows
 *    the cause and the target's flags (decision M3-D10).
 *  2. Phase 9 **prices** it: `base × nuclear × dualUse × roe`, in that order,
 *     rounded once at the end (errata #4, decision M3-D11).
 *  3. An entry whose mode is `ON_ATTRIBUTION` is banked unapplied and lands *in
 *     full, retroactively*, when attribution occurs (RB §7.4).
 */
import { MISSION_IDS } from '../model/enums.js';
function cause(c) {
    return Object.freeze({ conditional: false, ...c });
}
const CAUSE_LIST = Object.freeze([
    // Sensing and manoeuvre are free (RB §7.2, first block).
    cause({
        id: 'SDA_SENSING',
        value: 0,
        mode: 'NOT_APPLICABLE',
        summary: 'SDA sensing of any kind',
    }),
    cause({
        id: 'EW_SENSING',
        value: 0,
        mode: 'NOT_APPLICABLE',
        summary: 'Electronic Support, ELINT',
    }),
    cause({ id: 'ISR_COLLECTION', value: 0, mode: 'NOT_APPLICABLE', summary: 'ISR from orbit' }),
    cause({
        id: 'MANEUVER',
        value: 0,
        mode: 'NOT_APPLICABLE',
        summary: 'Manoeuvre, EMCON, Relocate, Evade',
    }),
    // Cyber intrusion is free until it is attributed — and then it is still free.
    cause({
        id: 'CYBER_RECONNOITER',
        value: 0,
        mode: 'ON_ATTRIBUTION',
        summary: 'Cyber Reconnoiter',
    }),
    cause({
        id: 'CYBER_GAIN_ACCESS',
        value: 0,
        mode: 'ON_ATTRIBUTION',
        summary: 'Cyber Gain Access',
    }),
    cause({
        id: 'CYBER_ESCALATE',
        value: 0,
        mode: 'ON_ATTRIBUTION',
        summary: 'Cyber Escalate Privilege',
    }),
    cause({
        id: 'CYBER_PERSISTENCE',
        value: 0,
        mode: 'ON_ATTRIBUTION',
        summary: 'Establish Persistence',
    }),
    // Orbital warfare.
    cause({ id: 'OW_SHADOW', value: 0, mode: 'OBSERVED', summary: 'Shadow' }),
    cause({
        id: 'OW_SHADOW_HVA',
        value: 1,
        mode: 'OBSERVED',
        // Only when the shadowed asset is an HVA, which the shadower may not know.
        conditional: true,
        summary: 'Shadow within one slot of an enemy HVA',
    }),
    cause({
        id: 'OW_RPO',
        value: 1,
        mode: 'OBSERVED',
        summary: 'Rendezvous and proximity operations',
    }),
    // Electromagnetic.
    cause({ id: 'EW_SPOOF', value: 1, mode: 'ON_ATTRIBUTION', summary: 'Spoof' }),
    cause({ id: 'EW_JAMMING', value: 2, mode: 'ON_ATTRIBUTION', summary: 'Jamming' }),
    cause({ id: 'EW_DAZZLE', value: 2, mode: 'ON_ATTRIBUTION', summary: 'Dazzling' }),
    // Cyber payloads.
    // All five bank inside `deliverPayload`, which `phase1-cyber.ts` reaches only on
    // a successful delivery. A Deploy Payload that fails its roll banks nothing.
    cause({
        id: 'CYBER_COLLECT',
        value: 1,
        mode: 'ON_ATTRIBUTION',
        conditional: true,
        summary: 'Cyber Collect',
    }),
    cause({
        id: 'CYBER_DENY',
        value: 2,
        mode: 'ON_ATTRIBUTION',
        conditional: true,
        summary: 'Cyber Deny',
    }),
    cause({
        id: 'CYBER_CORRUPT',
        value: 3,
        mode: 'ON_ATTRIBUTION',
        conditional: true,
        summary: 'Cyber Corrupt',
    }),
    cause({
        id: 'CYBER_HIJACK',
        value: 5,
        mode: 'ALWAYS',
        conditional: true,
        summary: 'Cyber Hijack (loud)',
    }),
    cause({
        id: 'CYBER_WIPER',
        value: 6,
        mode: 'ALWAYS',
        conditional: true,
        summary: 'Cyber Wiper (loud)',
    }),
    // Kinetic.
    cause({
        id: 'KKV_ATTEMPT',
        value: 4,
        mode: 'ON_ATTRIBUTION',
        summary: 'KKV engagement attempted',
    }),
    // `phase7-kinetic.ts` banks this inside `if (targetKilled)`. An engagement that
    // misses banks `KKV_ATTEMPT` and nothing else, which is why `ENGAGE` previews
    // with a floor of zero rather than of eight.
    cause({
        id: 'KKV_KILL',
        value: 8,
        mode: 'ALWAYS',
        conditional: true,
        summary: 'KKV engagement, target destroyed',
    }),
    cause({ id: 'DA_ASAT_LAUNCH', value: 6, mode: 'ALWAYS', summary: 'DA-ASAT launch' }),
    cause({
        id: 'GROUND_STRIKE',
        value: 7,
        mode: 'ALWAYS',
        summary: 'Kinetic attack on a ground target',
    }),
    cause({ id: 'ICBM_LAUNCH', value: 12, mode: 'ALWAYS', summary: 'ICBM launch' }),
    cause({ id: 'INTERCEPTION', value: 0, mode: 'ALWAYS', summary: 'Missile interception' }),
    // Missile warning judgement calls (RB §5.3.6, §7.2). M4 produces the releases;
    // the prices are here because they are escalation, not timing.
    cause({
        id: 'MW_BENIGN_RELEASE',
        value: 4,
        mode: 'NOT_APPLICABLE',
        // Only if the launch turns out to have been benign — the judgement the whole
        // cell exists to make, and the one thing it cannot check before making it.
        conditional: true,
        summary: 'MW releases on a benign launch',
    }),
    cause({
        id: 'FIRES_BENIGN_ENGAGEMENT',
        value: 10,
        mode: 'ALWAYS',
        conditional: true,
        summary: 'Joint Fires engages a benign launch',
    }),
    // Collateral.
    cause({
        id: 'NEUTRAL_DAMAGED',
        value: 8,
        mode: 'ALWAYS',
        conditional: true,
        summary: 'Neutral or third-party asset damaged',
    }),
    cause({
        id: 'DEBRIS_CEILING',
        value: 3,
        mode: 'NOT_APPLICABLE',
        conditional: true,
        summary: "A band's debris level reaches 5",
    }),
]);
export const ESCALATION_CAUSES = Object.freeze(Object.fromEntries(CAUSE_LIST.map((c) => [c.id, c])));
export function escalationCause(id) {
    return ESCALATION_CAUSES[id];
}
// ---------------------------------------------------------------------------
// What causes what (RB §7.2's first column, as a table) — M5 Q8
// ---------------------------------------------------------------------------
/**
 * The kinetic rows, keyed by what was fired. Phase 7 reads this; it used to hold
 * the same switch privately.
 *
 * Exhaustive over `WeaponTypeId` rather than defaulting, so that adding a weapon
 * type is a compile error here rather than a silent GROUND_STRIKE.
 */
export const WEAPON_ESCALATION_CAUSES = Object.freeze({
    SRBM: 'GROUND_STRIKE',
    IRBM: 'GROUND_STRIKE',
    HGV: 'GROUND_STRIKE',
    DA_ASAT: 'DA_ASAT_LAUNCH',
    ICBM: 'ICBM_LAUNCH',
    INTERCEPTOR: 'INTERCEPTION',
});
/**
 * The cyber intrusion rows (RB §7.2), keyed by mission. Phase 1 reads this.
 *
 * Partial on purpose: a cyber mission absent from the table banks nothing, which
 * is Hunt, Harden, Monitor, Isolate and Restore — defence is not escalatory.
 */
export const CYBER_INTRUSION_ESCALATION_CAUSES = Object.freeze({
    RECONNOITER: 'CYBER_RECONNOITER',
    GAIN_ACCESS: 'CYBER_GAIN_ACCESS',
    ESCALATE_PRIVILEGE: 'CYBER_ESCALATE',
    ESTABLISH_PERSISTENCE: 'CYBER_PERSISTENCE',
});
/** The payload rows (RB §7.2). One mission, `DEPLOY_PAYLOAD`, delivers all five. */
export const CYBER_PAYLOAD_ESCALATION_CAUSES = Object.freeze({
    COLLECT: 'CYBER_COLLECT',
    DENY: 'CYBER_DENY',
    CORRUPT: 'CYBER_CORRUPT',
    HIJACK: 'CYBER_HIJACK',
    WIPER: 'CYBER_WIPER',
});
/** The electromagnetic effects rows (RB §7.2). Phase 2 reads this. */
export const EM_ESCALATION_CAUSES = Object.freeze({
    JAMMING: 'EW_JAMMING',
    DAZZLE: 'EW_DAZZLE',
    SPOOF: 'EW_SPOOF',
});
/**
 * Mission → the `ESCALATION_CAUSES` rows it may draw — M5 Q8, for the order
 * builder's pre-submit preview.
 *
 * ## This is a hint, and never the number
 *
 * A cause's `value` is a **base**. Phase 9 prices an entry `base × nuclear ×
 * dualUse × roe`, rounded once at the end (decision M3-D11) — and the two flags in
 * that product belong to the *target*, which for an enemy asset the seat may not
 * know. An `ON_ATTRIBUTION` row banks unapplied and may never land at all. So the
 * client shows what this table says the act is worth before multipliers, labelled
 * as such, and the counter is the server's answer.
 *
 * ## Why several causes per mission
 *
 * Three missions cannot be resolved to one row from the mission alone: `LAUNCH`
 * depends on what the launcher fires, `DEPLOY_PAYLOAD` on the payload chosen, and
 * `ENGAGE` and `SHADOW` on what happens when they land. Listing every row a
 * mission may draw is honest about that; collapsing it to one would not be, and
 * the projection does not carry a launcher's weapon type for the client to narrow
 * it with.
 *
 * Composed from the tables above wherever one exists, so the preview cannot drift
 * from what the engine banks. The zero-valued rows are included even though
 * `bankEscalation` drops them: RB §7.2 lists them, "free" is a thing an operator
 * needs to be told, and Q7 asks for every modifier the seat has knowledge of.
 *
 * ## Reading a floor out of this list
 *
 * A caller wanting the *least* an order can add must count only rows that are
 * both applied on the spot (`mode !== 'ON_ATTRIBUTION'`) and banked whatever
 * happens (`!conditional`) — and zero when no row qualifies. `ENGAGE` is the case
 * that makes the distinction load-bearing: `KKV_ATTEMPT` is certain to be banked
 * and may never be charged, `KKV_KILL` is charged the moment it exists and may
 * never exist, and neither is a floor.
 */
const MISSION_CAUSES = {
    // SDA sensing (RB §7.2, first block) — free, and stated so.
    WIDE_SEARCH: ['SDA_SENSING'],
    SENSOR_TASK: ['SDA_SENSING'],
    CUSTODY_WATCH: ['SDA_SENSING'],
    CHARACTERIZE: ['SDA_SENSING'],
    DEBRIS_SURVEY: ['SDA_SENSING'],
    // Orbital manoeuvre is free; proximity is not.
    PHASE_SHIFT: ['MANEUVER'],
    TRANSFER: ['MANEUVER'],
    SHADOW: ['OW_SHADOW', 'OW_SHADOW_HVA'],
    RPO: ['OW_RPO'],
    ENGAGE: ['KKV_ATTEMPT', 'KKV_KILL'],
    BREAK_CUSTODY: [],
    DEPLOY: [],
    EMISSION_CONTROL: ['MANEUVER'],
    EVADE: ['MANEUVER'],
    // Electromagnetic.
    ELECTRONIC_SUPPORT: ['EW_SENSING'],
    ELINT: ['EW_SENSING'],
    JAMMING: [EM_ESCALATION_CAUSES.JAMMING],
    DAZZLE: [EM_ESCALATION_CAUSES.DAZZLE],
    ELECTRONIC_PROTECTION: [],
    SPOOF: [EM_ESCALATION_CAUSES.SPOOF],
    // Cyber.
    RECONNOITER: [CYBER_INTRUSION_ESCALATION_CAUSES.RECONNOITER],
    HUNT: [],
    GAIN_ACCESS: [CYBER_INTRUSION_ESCALATION_CAUSES.GAIN_ACCESS],
    ESCALATE_PRIVILEGE: [CYBER_INTRUSION_ESCALATION_CAUSES.ESCALATE_PRIVILEGE],
    DEPLOY_PAYLOAD: Object.values(CYBER_PAYLOAD_ESCALATION_CAUSES),
    ESTABLISH_PERSISTENCE: [CYBER_INTRUSION_ESCALATION_CAUSES.ESTABLISH_PERSISTENCE],
    HARDEN: [],
    MONITOR: [],
    ISOLATE: [],
    RESTORE: [],
    // Joint Fires. `LAUNCH` draws whichever row the launcher's round names.
    LAUNCH: [
        WEAPON_ESCALATION_CAUSES.SRBM,
        WEAPON_ESCALATION_CAUSES.DA_ASAT,
        WEAPON_ESCALATION_CAUSES.ICBM,
    ],
    INTERCEPT: [WEAPON_ESCALATION_CAUSES.INTERCEPTOR],
    // ISR.
    AREA_SEARCH: ['ISR_COLLECTION'],
    POINT_COLLECTION: ['ISR_COLLECTION'],
    DWELL: ['ISR_COLLECTION'],
    TARGET_DEVELOPMENT: ['ISR_COLLECTION'],
    BATTLE_DAMAGE_ASSESSMENT: ['ISR_COLLECTION'],
    PATTERN_OF_LIFE: ['ISR_COLLECTION'],
    // Ground manoeuvre and TT&C.
    RELOCATE: ['MANEUVER'],
    TTC_REASSIGN: [],
};
export const MISSION_ESCALATION_CAUSES = Object.freeze(Object.fromEntries(MISSION_IDS.map((id) => [id, Object.freeze([...new Set(MISSION_CAUSES[id])])])));
/**
 * The rows a mission may draw, resolved. Empty for a mission that never escalates.
 *
 * Returns the definitions rather than the ids so a caller renders the summary and
 * the base value without a second lookup — and so "this is a base" is visible in
 * the type it hands back.
 */
export function escalationPreviewFor(mission) {
    return MISSION_ESCALATION_CAUSES[mission]
        .map((id) => ESCALATION_CAUSES[id])
        .filter((def) => def !== undefined);
}
/**
 * The multiplier pipeline (errata #4, decision M3-D11).
 *
 * Ordered and named in **data** precisely because RB §7.2 leaves the interaction
 * undefined: the answer can change here without touching a resolver. The order is
 * `base × nuclear × dualUse × roe`, and rounding happens once at the end.
 */
export const MULTIPLIER_PIPELINE = Object.freeze([
    { source: 'NUCLEAR_ASSOCIATED', factor: 2 },
    { source: 'DUAL_USE', factor: 1.5 },
    { source: 'ROE_VIOLATION', factor: 2 },
]);
/**
 * Apply the pipeline. Returns the itemised multipliers alongside the value, because
 * an escalation entry is AAR product in exactly the way a `RollRecord` is: "+3
 * Corrupt, doubled for NUCLEAR-ASSOCIATED, ×1.5 for DUAL-USE = 9" is the sentence
 * a player needs to see.
 *
 * `Math.round` at the end, once. A +3 against a DUAL-USE target is 4.5 → 5. Pick
 * one and test it: the alternative is a number that differs between the ledger and
 * the counter.
 */
export function priceEscalation(base, applicable) {
    const multipliers = MULTIPLIER_PIPELINE.filter((m) => applicable.includes(m.source));
    const raw = multipliers.reduce((acc, m) => acc * m.factor, base);
    return { finalValue: Math.round(raw), multipliers };
}
// ---------------------------------------------------------------------------
// Decay (RB §7.1.1, errata #3)
// ---------------------------------------------------------------------------
/**
 * Errata #3 settles the literal reading: decay applies when **neither** team
 * generated escalation, not when either one was quiet.
 *
 * RB §7.1.1 then makes it a *streak*: −2 for the first quiet turn, −4 for the
 * second, "and by −4 again for each consecutive turn".
 */
export function decayFor(quietTurnsIncludingThisOne) {
    if (quietTurnsIncludingThisOne <= 0)
        return 0;
    return quietTurnsIncludingThisOne === 1 ? 2 : 4;
}
/** The counter never goes below zero. RB §7.1 has no negative band to land in. */
export const ESCALATION_FLOOR = 0;
// ---------------------------------------------------------------------------
// Rules of engagement (RB §7.3)
// ---------------------------------------------------------------------------
/** RB §7.3: a violation costs 3 VP. M3 records it; M6 scores it (decision M3-D16). */
export const ROE_VIOLATION_VP_PENALTY = 3;
export const ESCALATION_TABLE_MANIFEST = {
    ESCALATION_CAUSES,
    MULTIPLIER_PIPELINE,
    ROE_VIOLATION_VP_PENALTY,
    ESCALATION_FLOOR,
    MISSION_ESCALATION_CAUSES,
    WEAPON_ESCALATION_CAUSES,
};
//# sourceMappingURL=escalation.js.map